import { Image as ImageIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { TextInput } from '@/components/atoms/inputs'
import { ProfileModel } from '@/types'
import { getFileUrl } from '@/clients/http'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'
import { Spinner } from '@/components/ui/spinner'

interface EditProfileDialogProps {
    open: boolean
    profile: ProfileModel.Profile | null
    closeDialog: () => void
    onUpdate: (data: any) => void
    loading?: boolean
}

export const EditProfileDialog = ({
    open,
    profile,
    closeDialog,
    onUpdate,
    loading = false,
}: EditProfileDialogProps) => {
    const [galleryOpen, setGalleryOpen] = useState(false)
    const form = useForm({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            image_id: '',
            image_url_temp: '',
        },
    })

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: { errors },
    } = form

    const formValues = watch()

    useEffect(() => {
        if (profile) {
            reset({
                name: profile.name,
                email: profile.email || '',
                phone: profile.phone || '',
                image_id: profile.image?._id || '',
                image_url_temp: profile.image?.path || '',
            })
        }
    }, [profile, reset, open])

    const onSubmit = async (data: any) => {
        onUpdate({
            name: data.name,
            email: data.email,
            phone: data.phone,
            image_id: data.image_id,
        })
    }

    return (
        <>
            <Dialog open={open} onOpenChange={closeDialog}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>Edit profile</DialogTitle>
                        <DialogDescription>Update your profile information.</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Image Selection */}
                        <div className="flex flex-col items-center justify-center gap-3">
                            <div className="group relative flex size-24 items-center justify-center overflow-hidden rounded-full border border-dashed border-border bg-subdued transition-colors hover:border-ink">
                                {formValues.image_url_temp ? (
                                    <>
                                        <img
                                            src={getFileUrl(formValues.image_url_temp)}
                                            alt="Profile"
                                            className="size-full object-cover"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-ink/60 opacity-0 transition-opacity group-hover:opacity-100">
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="icon"
                                                className="rounded-full"
                                                aria-label="Change photo"
                                                onClick={() => setGalleryOpen(true)}
                                            >
                                                <ImageIcon />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                size="icon"
                                                className="rounded-full"
                                                aria-label="Remove photo"
                                                onClick={() => {
                                                    setValue('image_url_temp', '')
                                                    setValue('image_id', '')
                                                }}
                                            >
                                                <span className="text-sm font-semibold">×</span>
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="cursor-pointer space-y-1 p-4 text-center" onClick={() => setGalleryOpen(true)}>
                                        <ImageIcon className="mx-auto size-6 text-muted-foreground" />
                                        <span className="text-xs text-text-2">Add photo</span>
                                    </div>
                                )}
                            </div>
                            {!formValues.image_url_temp && (
                                <Button type="button" variant="outline" size="sm" onClick={() => setGalleryOpen(true)}>
                                    Select photo
                                </Button>
                            )}
                        </div>

                        <TextInput
                            control={control}
                            name="name"
                            label="Full name"
                            placeholder="John Doe"
                            rules={{ required: 'Name is required' }}
                            errors={errors}
                        />

                        <TextInput
                            control={control}
                            name="email"
                            label="Email"
                            placeholder="john@example.com"
                            rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: "Invalid email address"
                                }
                            }}
                            errors={errors}
                        />

                        <TextInput
                            control={control}
                            name="phone"
                            label="Phone"
                            placeholder="018xxxxxxx"
                            errors={errors}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleSubmit(onSubmit)} disabled={loading}>
                            {loading && <Spinner />}
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <MediaGalleryModal
                open={galleryOpen}
                onOpenChange={setGalleryOpen}
                onSelect={(files) => {
                    if (files.length > 0) {
                        setValue('image_url_temp', files[0].url)
                        setValue('image_id', files[0].id)
                    }
                }}
            />
        </>
    )
}
